<template>
  <AwSelectMenu
    ref="teamMemberSelectMenuRef"
    v-bind="selectMenuProps"
    v-model:search-term="searchTerm"
    :multiple="props.multiple"
    :placeholder="props.title"
    :items="teamMemberOptions"
    value-key="id"
    :content-class="`${props.contentWidth} max-h-85 ${loadingMore ? 'pb-2' : ''}`"
    :is-loading="loading || isRefreshing"
    :max-visible-items="props.maxVisibleItems"
    :search-input="!loading && isMounted"
    ignore-filter
    :model-value="loading ? undefined : modelValueForSelect"
    :is-item-selected="isComboboxItemSelected"
    @update:model-value="handleSelectionChange"
  >
    <template #default="{ modelValue }">
      <div v-if="!isMultiple">
        <div v-if="loading" class="flex flex-row items-center gap-2">
          <AwSkeleton class="h-5 w-5 rounded-full" />
          <AwSkeleton class="h-5 w-50 rounded-full" />
        </div>
        <div v-else-if="singleTriggerMember" class="flex flex-row items-center gap-2">
          <TeamMemberAvatar :team-member="singleTriggerMember" />
          {{ singleTriggerMember.label }}
        </div>
        <div v-else>
          {{ props.title }}
        </div>
      </div>
      <div v-else-if="Array.isArray(modelValue)">
        <SelectMenuBadgeList
          v-slot="{ item }"
          :title="props.title"
          icon="i-ph-user-circle"
          :loading="loading"
          :list="modelValue"
          :max-visible-items="maxVisibleBadgeCount"
          @update:list="
            if ($event) {
              handleSelectionChange($event);
            }
          "
        >
          {{ badgeLabelForSelectToken(item) }}
        </SelectMenuBadgeList>
      </div>
    </template>
    <template v-if="!lockedScope" #before-options>
      <TeamMemberSelectV2Tabs
        :model-value="activeFilter"
        :items="visibleFilterOptions"
        :default-value="defaultFilterValue"
        :is-multiple="isMultiple"
        :select-all-options="selectAllOptions"
        @update:model-value="setActiveFilter"
        @toggle-select-all-for-scope="onToggleSelectAllForScope"
      />
    </template>
    <template #item-label="{ item }">
      <TeamMemberSelectV2OptionRow :item="item" :loading-row-id="TEAM_MEMBER_SELECT_LOADING_ROW_ID" />
    </template>
  </AwSelectMenu>
</template>

<script setup lang="ts">
import { useTeamMemberSelectV2Data } from "~/components/team-member/composables/useTeamMemberSelectV2Data";
import { useUserMe } from "~/components/user/composables/useUser";
import { getFullName } from "@asap/shared/utils/people";
import type { AppRole } from "@asap/shared/utils/supabase.enum";
import type { TeamMemberSelectionEntry, TeamMemberTabScope } from "@asap/shared/schemas/team-member-select";
import { useTemplateRef } from "vue";
import {
  applyToggleSelectAllForScope,
  buildSelectAllOptionsForActiveTab,
  collapseManageesGranularFilters,
  decodeComboboxTokens,
  encodeEntriesToComboboxTokens,
  isManageesGranularScope,
  loadingSkeletonTeamMember,
  manageesGranularScopeBadgeLabel,
  memberMatchesTabScope,
  normalizeFilterVsExplicitIds,
  parseScopeFromComboboxFilterToken,
  parseTabValueToScope,
  selectionEntriesEqual,
  TEAM_MEMBER_SELECT_LOADING_ROW_ID,
  type TeamMemberOption,
} from "./team-member-select-v2.model";
import TeamMemberSelectV2OptionRow from "./TeamMemberSelectV2OptionRow.vue";
import TeamMemberSelectV2Tabs from "./TeamMemberSelectV2Tabs.vue";

type Props = {
  title?: string;
  roles?: AppRole[];
  contentWidth?: string;
  multiple?: boolean;
  maxVisibleItems?: number | "none";
  value?: string | TeamMemberSelectionEntry[];
  excludeIds?: string[];
  /** Keeps the option list on one tab and hides the tab bar. */
  lockedScope?: TeamMemberTabScope;
  /** Hides the "Sélectionner tous" rows, for callers that only keep explicit ids. */
  hideSelectAll?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  multiple: true,
  value: undefined,
  roles: undefined,
  title: "Consultants",
  contentWidth: "w-80",
  maxVisibleItems: 1,
  hideSelectAll: false,
});

type Emits = {
  "update:value": [value: string | TeamMemberSelectionEntry[] | undefined];
};

const emit = defineEmits<Emits>();

const selectMenuProps = computed(() => {
  const { lockedScope, hideSelectAll, ...menuProps } = props;
  void lockedScope;
  void hideSelectAll;
  return menuProps;
});

const isMultiple = computed(() => props.multiple !== false);

const selectionEntries = computed((): TeamMemberSelectionEntry[] => {
  if (!isMultiple.value) return [];
  const v = props.value;
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [];
});

const selectedIdsArray = computed((): string[] => {
  if (!isMultiple.value) {
    if (typeof props.value === "string" && props.value.length > 0) {
      return [props.value];
    }
    return [];
  }
  return selectionEntries.value.filter((e): e is { type: "id"; value: string } => e.type === "id").map((e) => e.value);
});

const filterScopesInSelection = computed(() =>
  selectionEntries.value
    .filter((e): e is { type: "filter"; value: TeamMemberTabScope } => e.type === "filter")
    .map((e) => e.value)
);

const { isIndependentAdministrator, isIndependent, isManager, isAccounting, isCSM, isConsultant } = useUserMe();

const tabLabelForScope = (scope: TeamMemberTabScope): string => {
  if (scope === "managees") return isManager.value ? "Moi & Managés" : "Managés";
  if (isManageesGranularScope(scope)) return manageesGranularScopeBadgeLabel(scope);
  if (scope === "consultants") return "Salariés";
  if (scope === "independents") return "Indépendants";
  return "Inactifs";
};

const labelForSelectToken = (token: string): string | undefined => {
  const scope = parseScopeFromComboboxFilterToken(token);
  return scope ? tabLabelForScope(scope) : undefined;
};

const maxVisibleBadgeCount = computed(() =>
  props.maxVisibleItems === "none" ? Number.POSITIVE_INFINITY : props.maxVisibleItems
);

const filterOptions = computed(() => [
  {
    label: "Salariés",
    value: "consultants",
    blocked: isIndependent.value === true,
  },
  {
    label: "Indépendants",
    value: "independents",
    blocked:
      isIndependentAdministrator.value === false &&
      isIndependent.value === false &&
      isManager.value === false &&
      isAccounting.value === false &&
      isCSM.value === false,
  },
  {
    label: "Managés",
    value: "managees",
    blocked: isManager.value === false && isIndependentAdministrator.value === false,
  },
  {
    label: "Inactifs",
    value: "inactives",
    blocked: false,
  },
]);

const visibleFilterOptions = computed(() =>
  filterOptions.value
    .filter((option) => !option.blocked)
    .map((option) => ({ label: option.label, value: option.value }))
);

const defaultFilterValue = computed(() => {
  if (props.lockedScope) return props.lockedScope;
  if (isManager.value === true || isIndependentAdministrator.value === true) {
    return "managees";
  }
  if (isConsultant.value === true || isAccounting.value === true || isCSM.value === true) return "consultants";
  if (isIndependent.value === true) {
    return "independents";
  }
  return "inactives";
});

const activeFilter = ref<string>(props.lockedScope ?? defaultFilterValue.value);

const teamMemberSelectMenuRef = useTemplateRef<{ getOptionsViewport: () => HTMLElement | null }>(
  "teamMemberSelectMenuRef"
);

const {
  loading,
  hasMore,
  searchTerm,
  isMounted,
  loadingMore,
  isRefreshing,
  availableOptions,
  selectedTeamMembers,
  manageeIds,
  isManageesLoaded,
} = useTeamMemberSelectV2Data({
  selectProps: props,
  isMultiple,
  selectedIdsArray,
  activeFilter,
  defaultFilterValue,
  teamMemberSelectMenuRef,
});

const memberScopeContext = computed(() => ({
  manageeIds: manageeIds.value,
  isManageesLoaded: isManageesLoaded.value,
  selectedIdsFallbackForManagees: selectedIdsArray.value,
}));

const teamMemberMatchesScope = (tm: TeamMemberOption, scope: TeamMemberTabScope): boolean =>
  memberMatchesTabScope(tm, scope, memberScopeContext.value);

const matchesActiveFilter = (teamMember: TeamMemberOption): boolean => {
  const scope = parseTabValueToScope(activeFilter.value);
  if (scope === null) return true;
  return memberMatchesTabScope(teamMember, scope, memberScopeContext.value);
};

const matchesSearchTerm = (teamMember: TeamMemberOption): boolean => {
  if (!searchTerm.value) return true;
  const searchLower = searchTerm.value.toLowerCase();
  return teamMember.label.toLowerCase().includes(searchLower);
};

const selectedOrderedForList = computed<TeamMemberOption[]>(() => {
  return selectedIdsArray.value
    .map((id) => selectedTeamMembers.value.find((tm) => tm.id === id))
    .filter((tm): tm is TeamMemberOption => Boolean(tm))
    .filter((tm) => matchesActiveFilter(tm) && matchesSearchTerm(tm));
});

const getTeamMemberDataForId = (id: string): TeamMemberOption | undefined => {
  const fromSelection = selectedTeamMembers.value.find((tm) => tm.id === id);
  if (fromSelection) return fromSelection;
  return selectedOrderedForList.value.find((tm) => tm.id === id) ?? availableOptions.value.find((tm) => tm.id === id);
};

const isRowDisabledByGlobalFilters = (tm: TeamMemberOption): boolean => {
  if (tm.id === TEAM_MEMBER_SELECT_LOADING_ROW_ID) return false;
  return filterScopesInSelection.value.some((scope) => teamMemberMatchesScope(tm, scope));
};

const isComboboxItemSelected = (item: TeamMemberOption): boolean => {
  if (!isMultiple.value) return false;
  if (item.id === TEAM_MEMBER_SELECT_LOADING_ROW_ID) return false;
  if (selectedIdsArray.value.includes(item.id)) return true;
  for (const scope of filterScopesInSelection.value) {
    if (teamMemberMatchesScope(item, scope)) return true;
  }
  return false;
};

const teamMemberOptions = computed<TeamMemberOption[]>(() => {
  const base = [...selectedOrderedForList.value, ...availableOptions.value].map((tm) => ({
    ...tm,
    disabled: isRowDisabledByGlobalFilters(tm),
  }));
  if (hasMore.value && availableOptions.value.length > 0) {
    base.push({ ...loadingSkeletonTeamMember, disabled: false });
  }
  return base;
});

const modelValueForSelect = computed(() => {
  if (!isMultiple.value) {
    if (typeof props.value === "string") return props.value;
    return undefined;
  }
  return encodeEntriesToComboboxTokens(selectionEntries.value);
});

const setActiveFilter = (val: unknown) => {
  activeFilter.value = typeof val === "string" ? val : String(val);
};

const getTeamMemberForTrigger = (id: string): TeamMemberOption | undefined => {
  const fromSelection = selectedTeamMembers.value.find((tm) => tm.id === id);
  if (fromSelection) return fromSelection;
  return teamMemberOptions.value.find((tm) => tm.id === id);
};

const singleTriggerMember = computed((): TeamMemberOption | undefined => {
  if (typeof props.value !== "string" || props.value.length === 0) return undefined;
  return getTeamMemberForTrigger(props.value);
});

const badgeLabelForSelectToken = (token: string): string => {
  const filterLabel = labelForSelectToken(token);
  if (filterLabel !== undefined) return filterLabel;
  const tm = getTeamMemberForTrigger(token);
  if (tm) return getFullName(tm);
  return token;
};

const handleSelectionChange = (val: string[] | string | undefined) => {
  if (!isMultiple.value) {
    if (typeof val === "string") {
      emit("update:value", val);
    } else if (Array.isArray(val) && val.length > 0) {
      const id = val.find((x) => x !== TEAM_MEMBER_SELECT_LOADING_ROW_ID);
      emit("update:value", id);
    } else {
      emit("update:value", undefined);
    }
    return;
  }
  const arr = Array.isArray(val) ? val : val === undefined ? [] : [val];
  const filtered = arr.filter((id) => id !== TEAM_MEMBER_SELECT_LOADING_ROW_ID);
  const parsed = decodeComboboxTokens(filtered);
  const normalized = collapseManageesGranularFilters(
    normalizeFilterVsExplicitIds(parsed, teamMemberOptions.value, {
      loadingSkeletonId: TEAM_MEMBER_SELECT_LOADING_ROW_ID,
      memberMatchesScope: (tm, scope) => memberMatchesTabScope(tm, scope, memberScopeContext.value),
    })
  );
  if (selectionEntriesEqual(selectionEntries.value, normalized)) return;
  emit("update:value", normalized.length === 0 ? undefined : normalized);
};

const selectAllOptions = computed(() =>
  props.hideSelectAll
    ? []
    : buildSelectAllOptionsForActiveTab(activeFilter.value, filterScopesInSelection.value, isMultiple.value)
);

const onToggleSelectAllForScope = (checked: boolean | "indeterminate", scope: TeamMemberTabScope) => {
  if (!isMultiple.value) return;

  const next = applyToggleSelectAllForScope({
    selectionEntries: selectionEntries.value,
    scope,
    checked,
    filterScopes: filterScopesInSelection.value,
    teamMemberMatchesScope,
    getTeamMemberById: getTeamMemberDataForId,
  });
  if (selectionEntriesEqual(selectionEntries.value, next)) return;
  emit("update:value", next.length === 0 ? undefined : next);
};

watch(
  selectionEntries,
  (entries) => {
    if (!isMultiple.value || entries.length === 0) return;
    const collapsed = collapseManageesGranularFilters(entries);
    if (selectionEntriesEqual(entries, collapsed)) return;
    emit("update:value", collapsed);
  },
  { immediate: true }
);

watch(
  visibleFilterOptions,
  (newOptions) => {
    if (props.lockedScope || !isMounted.value) return;
    const currentFilterIsVisible = newOptions.some((opt) => opt.value === activeFilter.value);
    if (!currentFilterIsVisible && newOptions.length > 0) {
      activeFilter.value = newOptions[0].value;
    }
  },
  { immediate: false }
);
</script>
